"use client";

import { useState, useEffect, useRef } from "react";
import { Link, usePathname } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import Image from "next/image";
import LanguageSwitcher from "./LanguageSwitcher";
import {
	FaBars,
	FaTimes,
	FaBriefcase,
	FaHandshake,
	FaComment,
	FaEnvelope,
	FaChartLine,
	FaUserPlus,
	FaChevronDown,
} from "react-icons/fa";

export default function Navbar() {
	const t = useTranslations("Navigation");
	const tFooter = useTranslations("Footer");
	const tCommon = useTranslations("Common");
	const pathname = usePathname();
	const [isOpen, setIsOpen] = useState(false);
	const [talentDropdownOpen, setTalentDropdownOpen] = useState(false);
	const [talentExpanded, setTalentExpanded] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);
	const talentDropdownRef = useRef<HTMLDivElement>(null);

	// Lock body scroll when mobile menu is open
	useEffect(() => {
		if (typeof document === "undefined") return;
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	const closeMenu = () => {
		setIsOpen(false);
		setTalentExpanded(false);
	};
	const toggleMenu = () => setIsOpen((prev) => !prev);

	const talentSubItems = [
		{ label: t("careersJobs"), href: "/careers", icon: FaBriefcase },
		{
			label: t("collaborations"),
			href: "/working-together",
			icon: FaHandshake,
		},
	];

	const topNavItems = [
		{
			key: "investment",
			label: t("bdtCapital"),
			href: "/investment",
			icon: FaChartLine,
		},
		{
			key: "talent",
			label: t("talent"),
			icon: FaUserPlus,
			subItems: talentSubItems,
		},
		{
			key: "testimonials",
			label: t("testimonials"),
			href: "/testimonials",
			icon: FaComment,
		},
		{ key: "contact", label: t("contact"), href: "/contact", icon: FaEnvelope },
	];

	const isActive = (href: string) =>
		pathname === href || (href !== "/" && pathname.startsWith(href));
	const isTalentActive =
		pathname === "/careers" || pathname.startsWith("/working-together");

	// Close dropdown when clicking outside
	useEffect(() => {
		if (!talentDropdownOpen) return;
		const handleClick = (e: MouseEvent) => {
			if (
				talentDropdownRef.current &&
				!talentDropdownRef.current.contains(e.target as Node)
			) {
				setTalentDropdownOpen(false);
			}
		};
		document.addEventListener("click", handleClick);
		return () => document.removeEventListener("click", handleClick);
	}, [talentDropdownOpen]);

	return (
		<nav className="bg-white shadow-md dark:bg-dark sticky top-0 z-50 font-sans min-h-[5.5rem] flex items-center py-4">
			<div className="w-full section-padding">
				<div className="flex justify-between items-center">
					<div className="flex items-center">
						<Link
							href="/"
							className="flex-shrink-0 no-underline group block transition-transform duration-300 hover:scale-105"
						>
							<Image
								src="/assets/business-development-together-logo.webp"
								alt={tFooter("companyName")}
								width={191}
								height={64}
								className="h-[54px] w-auto object-contain object-left transition-opacity group-hover:opacity-90"
								priority
							/>
						</Link>
					</div>

					<div className="hidden md:flex items-center font-bold text-[1.8rem] gap-2">
						{topNavItems.map((item) => {
							if ("subItems" in item && item.subItems) {
								const active = isTalentActive;
								return (
									<div
										key={item.key}
										ref={item.key === "talent" ? talentDropdownRef : undefined}
										className="relative"
									>
										<button
											type="button"
											onClick={() => setTalentDropdownOpen((prev) => !prev)}
											className={`flex items-center whitespace-nowrap py-[1.4rem] mx-[1rem] transition-all duration-200 rounded-full shrink-0 border-none bg-transparent cursor-pointer font-inherit ${
												active
													? "px-5 py-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50"
													: "nav-link px-3 py-2 text-[#212529] hover:text-bdt-blue dark:text-gray-200 dark:hover:text-bdt-blue hover:bg-gray-50 dark:hover:bg-gray-800/50"
											}`}
											aria-expanded={talentDropdownOpen}
											aria-haspopup="true"
										>
											<item.icon className="mr-2 transition-transform duration-200 hover:scale-110 shrink-0" />
											{item.label}
											<FaChevronDown
												className={`ml-1.5 shrink-0 transition-transform duration-200 ${
													talentDropdownOpen ? "rotate-180" : ""
												}`}
											/>
										</button>
										{talentDropdownOpen && (
											<div
												className="absolute top-full left-0 mt-1 py-2 min-w-[200px] rounded-xl bg-white dark:bg-gray-900 shadow-lg border border-gray-200 dark:border-gray-700 z-50"
												role="menu"
											>
												{item.subItems.map((sub) => {
													const subActive = isActive(sub.href);
													return (
														<Link
															key={sub.href}
															href={sub.href as any}
															onClick={() => setTalentDropdownOpen(false)}
															role="menuitem"
															className={`flex items-center gap-2 px-4 py-3 text-[1.6rem] no-underline whitespace-nowrap transition-colors ${
																subActive
																	? "bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200"
																	: "text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
															}`}
															aria-current={subActive ? "page" : undefined}
														>
															<sub.icon className="shrink-0" size={18} />
															{sub.label}
														</Link>
													);
												})}
											</div>
										)}
									</div>
								);
							}
							const active = isActive(item.href!);
							return (
								<Link
									key={item.key}
									href={item.href as any}
									className={`flex items-center whitespace-nowrap py-[1.4rem] mx-[1rem] no-underline transition-all duration-200 relative rounded-full shrink-0 ${
										active
											? "px-5 py-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50"
											: "nav-link px-3 py-2 text-[#212529] hover:text-bdt-blue dark:text-gray-200 dark:hover:text-bdt-blue hover:bg-gray-50 dark:hover:bg-gray-800/50"
									}`}
									aria-current={active ? "page" : undefined}
								>
									<item.icon className="mr-2 transition-transform duration-200 hover:scale-110 shrink-0" />
									{item.label}
								</Link>
							);
						})}
						<div className="ml-6 pl-4 border-l border-gray-200 dark:border-gray-600">
							<LanguageSwitcher />
						</div>
					</div>

					<div className="flex items-center md:hidden gap-3">
						<LanguageSwitcher />
						<button
							type="button"
							onClick={toggleMenu}
							className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg text-gray-700 dark:text-gray-200 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
							aria-expanded={isOpen}
							aria-label={isOpen ? tCommon("closeMenu") : tCommon("openMenu")}
						>
							{isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
						</button>
					</div>
				</div>
			</div>

			{/* Mobile menu: full-screen overlay + slide-in drawer */}
			<div
				className="md:hidden fixed inset-0 z-[100]"
				aria-hidden={!isOpen}
				style={{ pointerEvents: isOpen ? "auto" : "none" }}
			>
				{/* Backdrop */}
				<div
					className={`absolute inset-0 bg-black/50 dark:bg-black/60 transition-opacity duration-300 ${
						isOpen ? "opacity-100" : "opacity-0"
					}`}
					onClick={closeMenu}
					aria-hidden="true"
				/>

				{/* Drawer panel */}
				<div
					ref={menuRef}
					className={`absolute top-0 right-0 h-full w-full max-w-[min(320px,85vw)] bg-white dark:bg-gray-900 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
						isOpen ? "translate-x-0" : "translate-x-full"
					}`}
					role="dialog"
					aria-modal="true"
					aria-label="Navigation menu"
					data-drawer-open={isOpen}
				>
					{/* Drawer header */}
					<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
						<Link
							href="/"
							onClick={closeMenu}
							className="flex-shrink-0 no-underline block transition-transform duration-300 hover:scale-105"
						>
							<Image
								src="/assets/business-development-together-logo.webp"
								alt={tFooter("companyName")}
								width={191}
								height={64}
								className="h-[54px] w-auto object-contain object-left"
							/>
						</Link>
						<div className="flex items-center gap-3">
							<LanguageSwitcher />
							<button
								type="button"
								onClick={closeMenu}
								className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg text-gray-700 dark:text-gray-200 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
								aria-label={tCommon("closeMenu")}
							>
								<FaTimes size={22} />
							</button>
						</div>
					</div>

					{/* Nav links */}
					<nav className="flex-1 overflow-y-auto py-6 px-4">
						<ul className="space-y-1" role="list">
							{topNavItems.map((item, index) => {
								if ("subItems" in item && item.subItems) {
									const active = isTalentActive;
									return (
										<li
											key={item.key}
											className="nav-drawer-item"
											style={{ animationDelay: `${index * 60}ms` }}
										>
											<button
												type="button"
												onClick={() => setTalentExpanded((prev) => !prev)}
												className={`flex items-center gap-4 px-4 py-4 rounded-xl text-[1.8rem] font-bold w-full min-h-[52px] transition-all duration-200 border-l-4 whitespace-nowrap border-none bg-transparent cursor-pointer text-left ${
													active
														? "text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20 border-blue-500"
														: "text-gray-900 dark:text-gray-100 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 border-transparent"
												}`}
												aria-expanded={talentExpanded}
											>
												<span
													className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 transition-colors duration-200 ${
														active
															? "bg-blue-200/50 dark:bg-blue-800/50 text-blue-800 dark:text-blue-200"
															: "bg-gray-100 dark:bg-gray-800 text-primary"
													}`}
												>
													<item.icon size={20} />
												</span>
												<span className="min-w-0 truncate flex-1">
													{item.label}
												</span>
												<FaChevronDown
													className={`shrink-0 transition-transform duration-200 ${
														talentExpanded ? "rotate-180" : ""
													}`}
												/>
											</button>
											{talentExpanded && (
												<ul className="mt-1 ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-1">
													{item.subItems.map((sub) => {
														const subActive = isActive(sub.href);
														return (
															<li key={sub.href}>
																<Link
																	href={sub.href as any}
																	onClick={closeMenu}
																	className={`flex items-center gap-3 px-4 py-3 rounded-lg text-[1.5rem] font-semibold no-underline min-h-[44px] transition-all ${
																		subActive
																			? "text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20"
																			: "text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
																	}`}
																	aria-current={subActive ? "page" : undefined}
																>
																	<sub.icon size={18} className="shrink-0" />
																	<span className="min-w-0 truncate">
																		{sub.label}
																	</span>
																</Link>
															</li>
														);
													})}
												</ul>
											)}
										</li>
									);
								}
								const active = isActive(item.href!);
								return (
									<li
										key={item.key}
										className="nav-drawer-item"
										style={{ animationDelay: `${index * 60}ms` }}
									>
										<Link
											href={item.href as any}
											onClick={closeMenu}
											className={`flex items-center gap-4 px-4 py-4 rounded-xl text-[1.8rem] font-bold no-underline min-h-[52px] transition-all duration-200 border-l-4 whitespace-nowrap ${
												active
													? "text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20 border-blue-500"
													: "text-gray-900 dark:text-gray-100 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 border-transparent"
											}`}
											aria-current={active ? "page" : undefined}
										>
											<span
												className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 transition-colors duration-200 ${
													active
														? "bg-blue-200/50 dark:bg-blue-800/50 text-blue-800 dark:text-blue-200"
														: "bg-gray-100 dark:bg-gray-800 text-primary"
												}`}
											>
												<item.icon size={20} />
											</span>
											<span className="min-w-0 truncate">{item.label}</span>
										</Link>
									</li>
								);
							})}
						</ul>
					</nav>
				</div>
			</div>
		</nav>
	);
}
